<template>
  <section>
    <div
      v-for="(transaction, transactionIdx) in transactionsClean"
      :key="transaction.id"
      class="flex flex-col mt-4"
    >
      <div class="font-label text-fg-secondary text-xs uppercase">
        {{ settingStore.dateTimeStr(transaction.created) }}
        <span v-if="transaction.isManualEdit"> - Manual Edit</span>
      </div>
      <template v-if="Number(transaction.refunded) <= 0">
        <TransactionLine
          name="Tickets"
          :uid="`transaction-${transaction.id}`"
          :amount="Number(transaction.amount)"
          :label="transaction.label"
        />
        <template v-if="addOns && transactionIdx === 0">
          <TransactionLine
            v-for="addOn in addOnsClean"
            :key="addOn.performance_add_on_id"
            :name="addOn.name"
            :uid="`transaction-${transaction.id}-addon-${addOn.performance_add_on_id}`"
            :amount="Number(addOn.amount)"
            :label="addOn.label"
          />
        </template>
        <TransactionLine
          name="Service Fee"
          :uid="`transaction-${transaction.id}-service-fee`"
          :amount="Number(transaction.service_fee)"
        />
        <TransactionLine
          name="Tax"
          :uid="`transaction-${transaction.id}-tax`"
          :amount="Number(transaction.tax)"
        />
        <template v-if="Number(transaction.discounted) > 0 && promotion">
          <TransactionLine
            :name="`Discounted (${promotion.name} ${promotion.coupon.name})`"
            :uid="`transaction-${transaction.id}-discounted`"
            :amount="-Number(transaction.discounted)"
          />
        </template>
        <TransactionLine
          name="Total"
          :uid="`transaction-${transaction.id}-total`"
          :amount="Number(transaction.total)"
        />
      </template>
      <template v-else>
        <TransactionLine
          name="Refund"
          :uid="`transaction-${transaction.id}-refund`"
          :amount="-Number(transaction.refunded)"
        />
      </template>
    </div>
  </section>
</template>
<script lang="ts">
import { mapState, mapStores } from 'pinia'
import { PropType, defineComponent } from 'vue'

import TransactionLine from '@/components/transactions/TransactionLine.vue'
import { useSettingStore } from '@/stores/setting'

import { PromotionCodeRead, ReservationAddOnRead, TransactionDetailedRead } from '@generated/types'

interface DecoratedTransaction extends TransactionDetailedRead {
  isManualEdit: boolean
  label?: string
  total?: number
}

interface ReservationAddOns extends ReservationAddOnRead {
  name: string
  label?: string
  amount?: number
}

export default defineComponent({
  name: 'ReservationTransactions',
  components: {
    TransactionLine,
  },
  props: {
    addOns: {
      type: Array as PropType<ReservationAddOns[]>,
      required: false,
    },
    addOnAmount: {
      type: Number,
      required: false,
    },
    promotion: {
      type: Object as PropType<PromotionCodeRead>,
      required: false,
    },
    transactions: {
      type: Array as PropType<DecoratedTransaction[]>,
      required: true,
    },
  },
  computed: {
    ...mapStores(useSettingStore),
    ...mapState(useSettingStore, ['currency']),

    addOnsClean(): ReservationAddOns[] | null {
      if (!this.addOns) return null
      return this.addOns.map(addOn => {
        return {
          ...addOn,
          label: `(${addOn.quantity} @ ${this.currency(addOn.price)})`,
          amount: addOn.quantity * addOn.price,
        }
      })
    },

    transactionsClean(): DecoratedTransaction[] {
      return this.transactions.map((transaction, transactionIdx) => {
        const numberOfGuests = transaction.number_of_guests || 0
        const transactionTotal =
          Number(transaction.tax) + Number(transaction.amount) + Number(transaction.service_fee)
        return {
          ...transaction,
          label:
            (transaction.number_of_guests || 0) > 0
              ? `(${numberOfGuests} @ ${this.currency(transaction.amount / numberOfGuests)})`
              : '',
          total:
            this.addOnAmount && transactionIdx === 0
              ? transactionTotal + this.addOnAmount
              : transactionTotal,
        }
      })
    },
  },
})
</script>
